import React from 'react'
import {ActivityIndicator, StyleSheet} from 'react-native'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useFocusEffect} from '@react-navigation/native'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {type CommonNavigatorParams} from '#/lib/routes/types'
import {useSetMinimalShellMode} from '#/state/shell'
import {useTapTagsQuery} from '#/state/queries/tap-tags'
import {type TapTag} from '#/lib/api/tap/types'
import {TagFeedPage} from '#/view/com/tap/TagFeedPage'
import {MediaFeedPage} from '#/view/com/tap/MediaFeedPage'
import {PostsHeader} from '#/view/com/tap/PostsHeader'
import {
  Pager,
  type PagerRef,
  type RenderTabBarFnProps,
} from '#/view/com/pager/Pager'
import {emitSoftReset} from '#/state/events'
import * as Layout from '#/components/Layout'
import {useNonReactiveCallback} from '#/lib/hooks/useNonReactiveCallback'
import {logEvent} from '#/lib/statsig/statsig'

export function PostsScreen({
  route,
}: NativeStackScreenProps<CommonNavigatorParams, 'Posts'>) {
  const {_} = useLingui()
  const setMinimalShellMode = useSetMinimalShellMode()
  const {data: tags, isLoading} = useTapTagsQuery()

  useFocusEffect(
    React.useCallback(() => {
      setMinimalShellMode(false)
    }, [setMinimalShellMode]),
  )

  if (isLoading || !tags) {
    return (
      <Layout.Screen>
        <Layout.Center style={styles.loading}>
          <ActivityIndicator size="large" color="#FD7C21" />
        </Layout.Center>
      </Layout.Screen>
    )
  }

  return (
    <Layout.Screen testID="PostsScreen">
      <PostsScreenReady tags={tags} initialTag={route.params?.tag} />
    </Layout.Screen>
  )
}

type FeedTab =
  | {type: 'tag'; tag: TapTag}
  | {type: 'media'; mediaType: 'images' | 'videos'; name: string}

function PostsScreenReady({
  tags,
  initialTag,
}: {
  tags: TapTag[]
  initialTag?: string
}) {
  const {_} = useLingui()
  const setMinimalShellMode = useSetMinimalShellMode()

  // Create tabs: Videos first, then all tags (excluding 'ccad') sorted alphabetically
  const tabs: FeedTab[] = React.useMemo(() => {
    const sortedTags = [...tags]
      .filter(tag => tag.slug !== 'ccad')
      .sort((a, b) => a.name.localeCompare(b.name))
    const result = [
      {type: 'media' as const, mediaType: 'videos' as const, name: 'Videos'},
      ...sortedTags.map(tag => ({type: 'tag' as const, tag})),
    ]
    console.log('[PostsScreen] Tabs created:', result.length)
    return result
  }, [tags])

  // Tab names for header
  const tabNames = React.useMemo(() => {
    return tabs.map(tab => {
      if (tab.type === 'media') return tab.name
      return tab.tag.name
    })
  }, [tabs])

  // Find initial index based on tag param or default to first tag
  const initialIndex = React.useMemo(() => {
    if (initialTag) {
      const index = tabs.findIndex(tab => tab.type === 'tag' && tab.tag.slug === initialTag)
      return index >= 0 ? index : 0
    }
    return 0
  }, [tabs, initialTag])

  const [selectedIndex, setSelectedIndex] = React.useState(initialIndex)
  const selectedTab = tabs[selectedIndex]

  // Respond to tag changes from navigation (e.g., clicking tag in right nav)
  React.useEffect(() => {
    if (initialTag) {
      const newIndex = tabs.findIndex(
        tab => tab.type === 'tag' && tab.tag.slug === initialTag,
      )
      if (newIndex >= 0 && newIndex !== selectedIndex) {
        setSelectedIndex(newIndex)
      }
    }
  }, [initialTag, tabs, selectedIndex])

  console.log('[PostsScreen] Render:', {
    selectedIndex,
    selectedTab: selectedTab?.type === 'media'
      ? selectedTab.name
      : selectedTab?.tag.name,
    totalTabs: tabs.length,
  })

  const pagerRef = React.useRef<PagerRef>(null)
  const lastPagerReportedIndexRef = React.useRef(selectedIndex)

  React.useLayoutEffect(() => {
    if (selectedIndex !== lastPagerReportedIndexRef.current) {
      lastPagerReportedIndexRef.current = selectedIndex
      pagerRef.current?.setPage(selectedIndex)
    }
  }, [selectedIndex])

  useFocusEffect(
    useNonReactiveCallback(() => {
      if (selectedTab) {
        if (selectedTab.type === 'tag') {
          logEvent('posts:tagDisplayed', {
            index: selectedIndex,
            tagSlug: selectedTab.tag.slug,
            tagName: selectedTab.tag.name,
            reason: 'focus',
          })
        } else if (selectedTab.type === 'media') {
          logEvent('posts:mediaDisplayed', {
            index: selectedIndex,
            mediaType: selectedTab.mediaType,
            reason: 'focus',
          })
        }
      }
    }),
  )

  const onPageSelected = React.useCallback(
    (index: number) => {
      setMinimalShellMode(false)
      const tab = tabs[index]

      lastPagerReportedIndexRef.current = index
      setSelectedIndex(index)

      if (tab) {
        if (tab.type === 'tag') {
          logEvent('posts:tagDisplayed', {
            index,
            tagSlug: tab.tag.slug,
            tagName: tab.tag.name,
          })
        } else if (tab.type === 'media') {
          logEvent('posts:mediaDisplayed', {
            index,
            mediaType: tab.mediaType,
          })
        }
      }
    },
    [setMinimalShellMode, tabs],
  )

  const onPressSelected = React.useCallback(() => {
    emitSoftReset()
  }, [])

  const onPageScrollStateChanged = React.useCallback(
    (state: 'idle' | 'dragging' | 'settling') => {
      'worklet'
      if (state === 'dragging') {
        setMinimalShellMode(false)
      }
    },
    [setMinimalShellMode],
  )

  const renderTabBar = React.useCallback(
    (props: RenderTabBarFnProps) => {
      return (
        <PostsHeader
          key="POSTS_TAB_BAR"
          {...props}
          testID="postsScreenTabs"
          onPressSelected={onPressSelected}
          tabNames={tabNames}
        />
      )
    },
    [onPressSelected, tabNames],
  )

  return (
    <Pager
      key={tabs.map(t => {
        if (t.type === 'media') return t.mediaType
        return t.tag.slug
      }).join(',')}
      ref={pagerRef}
      testID="postsScreen"
      initialPage={selectedIndex}
      onPageSelected={onPageSelected}
      onPageScrollStateChanged={onPageScrollStateChanged}
      renderTabBar={renderTabBar}>
      {tabs.map((tab, index) => {
        console.log('[PostsScreen] Rendering tab:', {
          index,
          type: tab.type,
          name: tab.type === 'media' ? tab.name : tab.tag.name,
          isPageFocused: selectedIndex === index,
          isPageAdjacent: Math.abs(selectedIndex - index) === 1,
        })

        if (tab.type === 'media') {
          return (
            <MediaFeedPage
              key={tab.mediaType}
              testID={`mediaFeedPage-${tab.mediaType}`}
              mediaType={tab.mediaType}
              isPageFocused={selectedIndex === index}
              isPageAdjacent={Math.abs(selectedIndex - index) === 1}
            />
          )
        } else {
          return (
            <TagFeedPage
              key={tab.tag.slug}
              testID={`tagFeedPage-${tab.tag.slug}`}
              tagSlug={tab.tag.slug}
              tagName={tab.tag.name}
              isPageFocused={selectedIndex === index}
              isPageAdjacent={Math.abs(selectedIndex - index) === 1}
            />
          )
        }
      })}
    </Pager>
  )
}

const styles = StyleSheet.create({
  loading: {
    height: '100%',
    alignContent: 'center',
    justifyContent: 'center',
    paddingBottom: 100,
  },
})
